/**
 * Philosophy Section Component
 *
 * Displays the philosophical understanding layer for a concept.
 * Teaches HOW TO THINK about a concept, not just HOW TO WRITE it.
 *
 * @module concept-graph/components
 */

import React from 'react';
import { Brain, Lightbulb, TrendingUp, AlertTriangle, Sparkles } from 'lucide-react';
import type { ConceptPhilosophy } from '../types';

export interface PhilosophySectionProps {
  philosophy: ConceptPhilosophy;
  className?: string;
}

/**
 * Display philosophical understanding of a concept
 */
export function PhilosophySection({
  philosophy,
  className = ''
}: PhilosophySectionProps) {
  return (
    <div className={`space-y-6 ${className}`}>
      {/* Core Problem */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-lg border border-blue-200">
        <div className="flex items-start gap-3">
          <Brain className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Core Problem</h4>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
              {philosophy.coreProblem}
            </p>
          </div>
        </div>
      </div>

      {/* Fundamental Trade-off */}
      {philosophy.fundamentalTradeoff && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-5 rounded-lg border border-amber-200">
          <div className="flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-amber-900 mb-2">The Fundamental Trade-off</h4>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                {philosophy.fundamentalTradeoff}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Language Philosophies */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-5 rounded-lg border border-purple-200">
        <div className="flex items-start gap-3 mb-4">
          <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <h4 className="text-sm font-semibold text-purple-900">Design Philosophies</h4>
        </div>
        <div className="space-y-4">
          {Object.entries(philosophy.languagePhilosophies).map(([lang, phil]) => (
            <div key={lang} className="bg-white p-4 rounded-lg border border-purple-100">
              <div className="font-semibold text-slate-900 mb-2">{lang}</div>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-slate-600">Approach:</span>
                  <span className="ml-2 text-slate-800 font-medium">"{phil.approach}"</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-slate-600 flex-shrink-0">Why:</span>
                  <span className="text-slate-700">{phil.why}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-slate-600 flex-shrink-0">Trade-off:</span>
                  <span className="text-slate-700">{phil.tradeoff}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mental Model */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-5 rounded-lg border border-green-200">
        <div className="flex items-start gap-3">
          <Lightbulb className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-green-900 mb-2">Mental Model</h4>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap italic">
              "{philosophy.mentalModel}"
            </p>
          </div>
        </div>
      </div>

      {/* When to Use */}
      {philosophy.whenToUse && Object.keys(philosophy.whenToUse).length > 0 && (
        <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-5 rounded-lg border border-cyan-200">
          <h4 className="text-sm font-semibold text-cyan-900 mb-3">When to Choose What</h4>
          <div className="space-y-3">
            {Object.entries(philosophy.whenToUse).map(([approach, scenarios]) => (
              <div key={approach} className="bg-white p-3 rounded border border-cyan-100">
                <div className="text-sm font-medium text-slate-900 mb-2">{approach}:</div>
                <ul className="space-y-1">
                  {scenarios.map((scenario, idx) => (
                    <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                      <span className="text-cyan-500 flex-shrink-0">•</span>
                      <span>{scenario}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Common Pitfalls */}
      {philosophy.commonPitfalls && philosophy.commonPitfalls.length > 0 && (
        <div className="bg-gradient-to-r from-red-50 to-rose-50 p-5 rounded-lg border border-red-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-red-900 mb-3">Common Conceptual Pitfalls</h4>
              <ul className="space-y-2">
                {philosophy.commonPitfalls.map((pitfall, idx) => (
                  <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                    <span className="text-red-500 flex-shrink-0">⚠️</span>
                    <span>{pitfall}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Key Insights */}
      {philosophy.keyInsights && philosophy.keyInsights.length > 0 && (
        <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-5 rounded-lg border border-yellow-200">
          <h4 className="text-sm font-semibold text-yellow-900 mb-3">Key Insights</h4>
          <div className="space-y-2">
            {philosophy.keyInsights.map((insight, idx) => (
              <div key={idx} className="bg-white p-3 rounded border border-yellow-100 text-sm text-slate-700">
                <span className="text-yellow-600 font-medium mr-2">💡</span>
                {insight}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default PhilosophySection;
